import { AppBarProps } from '@mui/material/AppBar';
import { Box } from '@atoms/layout';

import { useLocationStore } from '@libs/store/locations';
import { useEffect, useState } from 'react';
import { Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import { getTimeZones } from '@vvo/tzdb';
import dayjs from 'dayjs';
import { TIMEZONE_FALLBACK } from '@libs/config';
import { HeaderSelect } from '@atoms/inputs';

export const HeaderLocationSelector = ({ ...props }: AppBarProps) => {
  const [timezoneLabel, setTimezoneLabel] = useState<string>('');
  const [timezoneTooltip, setTimezoneTooltip] = useState<string>('');
  const [timezone, setTimezoneName] = useState<string>(TIMEZONE_FALLBACK);
  const [tzAbbreviation, setTzAbbreviation] = useState<string>('');
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);

  const {
    locationsInitiated,
    locations,
    loadLocations,
    loading: locationsLoading,
    loadCurrentWorkingLocation,
    currentWorkingLocationDetails,
    currentWorkingLocationId,
  } = useLocationStore();

  const loading = locationsLoading;
  const txPreparing = loading || !tzAbbreviation || !timezoneLabel;

  const locationsOptions = locations
    .filter((location) => location.status === 'Active')
    .map((loc) => ({
      key: loc.id,
      value: loc.id,
      label: loc.name,
    }));

  const locationValue = currentWorkingLocationId ??  locationsOptions[0]?.value ?? '';

  useEffect(() => {
    // setup infinite timer as a trigger for state updates
    setInterval(() => {
      setLastUpdate(new Date());
    }, 1000 * 60);
  }, []);

  useEffect(() => {
    if (!locationsInitiated) {
      loadLocations();
    }
  }, [locationsInitiated, loadLocations]);

  useEffect(() => {
    if (!currentWorkingLocationDetails) {
      if(currentWorkingLocationId && locationsOptions.some((loc) => loc.value === currentWorkingLocationId)){
        loadCurrentWorkingLocation(currentWorkingLocationId);
      } else if(locationsOptions[0]?.value){
        loadCurrentWorkingLocation(locationsOptions[0].value);
      }
    }
  }, [currentWorkingLocationDetails, currentWorkingLocationId]);

  useEffect(() => {
    if (currentWorkingLocationDetails) {
      const currentTZData = getTimeZones({ includeUtc: true }).find(
        (tz) => tz.name === currentWorkingLocationDetails.timeZone,
      );
      if (currentTZData) {
        setTimezoneTooltip(currentTZData.currentTimeFormat);
        setTimezoneName(currentTZData.name);
        setTzAbbreviation(currentTZData.abbreviation);
        const shift = dayjs(new Date()).tz(currentTZData.name).format('hh:mm A');
        setTimezoneLabel(`${shift} (${currentTZData.abbreviation})`);
      }
    }
  }, [currentWorkingLocationDetails]);

  useEffect(() => {
    const shift = dayjs(new Date()).tz(timezone).format('hh:mm A');
    setTimezoneLabel(`${shift} (${tzAbbreviation})`);
  }, [lastUpdate, tzAbbreviation, timezone]);

  return (
    <Stack direction={'row'}>
      <Box>
        {loading ? (
          <Skeleton variant="rounded" animation="wave" height={48} width={220} />
        ) : (
          <HeaderSelect
            options={locationsOptions}
            fullWidth
            sx={{ minWidth: '220px' }}
            value={locationValue}
            onChange={(e) => loadCurrentWorkingLocation(e.target.value)}
          />
        )}
      </Box>
      <Stack direction={'column'} sx={{ minWidth: '120px' }} spacing={0.5} ml={2}>
        <Box flex={1} />
        {txPreparing ? (
          <Skeleton width={'150px'} />
        ) : (
          <Tooltip title={timezoneTooltip} sx={{ minWidth: '150px' }}>
            <Typography variant="subtitle1">{timezoneLabel}</Typography>
          </Tooltip>
        )}
        <Box flex={1} />
      </Stack>
    </Stack>
  );
};
